import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material";
import {
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Grid,
  Divider,
  Chip,
  Box,
  Button,
  FormHelperText,
  Typography,
  Container,
  Card,
  CardContent,
  Breadcrumbs,
  Link,
  Autocomplete,
  Modal,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import dayjs from "dayjs";
import "dayjs/locale/es";
import Restricted from "components/Restricted";

import HomeIcon from "@mui/icons-material/Home";
import DepartureBoardOutlined from "@mui/icons-material/DepartureBoardOutlined";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";
import AssignmentIcon from "@mui/icons-material/Assignment";
import Face3Icon from "@mui/icons-material/Face3";

// Función para capitalizar la primera letra de cada palabra
const capitalizeWords = (str) => {
  if (!str) return "";
  return str
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

// Mapeos y rangos para la lógica de códigos
const grupoEtnicoMap = {
  1: "Mestizo",
  2: "Wayuu",
  3: "Añú",
  4: "Japreira",
  5: "Yanomami",
  6: "Bari",
};
const grupoEtnicoRange = { min: 1, max: 6 };

const discapacidadMap = {
  1: "Física",
  2: "Intelectual",
  3: "Sensorial",
  4: "Múltiple",
  5: "Ninguna",
};
const discapacidadRange = { min: 1, max: 5 };

const condicionMovilidadMap = {
  1: "Local",
  2: "Desplazado Internos",
  3: "Refugiado",
  4: "Retornado",
  5: "Pendular",
};
const condicionMovilidadRange = { min: 1, max: 5 };

const factoresRiesgoMap = {
  1: "Abuso Psicológico",
  2: "Abuso Físico",
  3: "Abuso Sexual",
  4: "Embarazo Adolescente",
  5: "Explotación Sexual",
  6: "Falta de Documento de Identidad",
  7: "Matrimonio Infantil",
  8: "Negligencia",
  9: "No Acompañado (< 18 años)",
  10: "Trabajo Infantil",
  11: "Trastorno Mental",
  12: "No Aplica",
};
const factoresRiesgoRange = { min: 1, max: 12 };

const nivelEducativoMap = {
  1: "Prescolar",
  2: "Primaria",
  3: "Secundaria",
  4: "Universitario",
  5: "Sin Estudios",
  6: "Analfabeta",
};
const nivelEducativoRange = { min: 1, max: 6 };

const metodoAnticonceptivoMap = {
  1: "Orales",
  2: "Implantes de 3 años",
  3: "Implantes de 5 años",
  4: "Inyecciones Trimestrales",
  5: "Inyecciones Mensuales",
  6: "DIU",
  7: "Preservativo",
};
const metodoAnticonceptivoRange = { min: 1, max: 7 };

const diagnosticoItsMap = {
  1: "Vaginosis Bacteriana",
  2: "Vaginitis Micótica",
  3: "Vaginitis y Cervicitis",
  4: "Sífilis Primaria",
  5: "Sífilis Secundaria",
  6: "Úlcera Vaginal",
  7: "Chancro Blando",
  8: "Herpes Genital",
  9: "Linfogranuloma Venéreo",
  10: "Granuloma Inguinal",
  11: "Secreción Uretral",
  12: "VIH",
  13: "VPH",
  14: "Hepatitis B",
  15: "N/A",
};
const diagnosticoItsRange = { min: 1, max: 15 };

// Función para limpiar espacios en blanco de todos los strings del formulario
const trimFormData = (data) => {
  const trimmedData = {};
  for (const key in data) {
    const value = data[key];
    if (typeof value === "string") {
      trimmedData[key] = value.trim();
    } else if (
      value &&
      typeof value === "object" &&
      !Array.isArray(value) &&
      !dayjs.isDayjs(value)
    ) {
      trimmedData[key] = trimFormData(value);
    } else {
      trimmedData[key] = value;
    }
  }
  return trimmedData;
};

// Opciones de prueba para el Autocomplete
const medicosDePrueba = [
  { label: "Dr. Juan Pérez" },
  { label: "Dra. Ana García" },
  { label: "Dr. Carlos López" },
];

function AntiConForm() {
  const [items, setItems] = React.useState([]);

  // Estados para la nueva sección de Info General
  const [estadoSalud, setEstadoSalud] = useState("");
  const [municipioSalud, setMunicipioSalud] = useState("");
  const [parroquiaSalud, setParroquiaSalud] = useState("");
  const [estSalud, setEstSalud] = useState("");
  const [fechaActSalud, setFechaActSalud] = useState(dayjs());

  useEffect(() => {
    const items = JSON.parse(localStorage.getItem("token"));
    if (items) {
      setItems(items);
      // Poblar los campos de Info General desde el token
      setEstadoSalud(items.estado || "");
      setMunicipioSalud(items.municipio || "");
      setParroquiaSalud(items.parroquia || "");
      setEstSalud(items.estSalud || "");
    }
  }, []);

  const rol = items.rol;
  const nav = useNavigate();
  const theme = useTheme();

  const [formData, setFormData] = useState({
    beneficiaria: {
      codigo: "",
      primerNombre: "",
      primerApellido: "",
      genero: "",
      cedulaIdentidad: "",
      numeroTelefonico: "",
      fechaNacimiento: null,
    },
    representante: {
      primerNombre: "",
      primerApellido: "",
      cedulaIdentidad: "",
    },
    demograficosBeneficiaria: {
      lgbtqi: "",
      comunidad: "",
      codigoComunidad: "",
      grupoEtnico: "",
      codigoGrupoEtnico: "",
      discapacidad: "",
      codigoDiscapacidad: "",
      condicionMovilidad: "",
      codigoCondicionMovilidad: "",
      factoresRiesgo: "",
      codigoFactoresRiesgo: "",
      nivelEducativo: "",
      ingresosPropios: "",
      cabezaFamilia: "",
    },
    atencion: {
      pruebaEmbarazo: "",
      codigoAtencion: "",
      metodoAnticonceptivo: "",
    },
    manejoITS: {
      pruebaVIH: "",
      pruebaSifilis: "",
      pruebaHepatitis: "",
      codigoDiagnosticoITS: "",
      diagnosticoITS: "",
      tratamientoITS: "",
    },
    adicionales: { comentarios: "", medicoResponsable: null },
  });
  const [errors, setErrors] = useState({});
  const [isRepresentanteDisabled, setIsRepresentanteDisabled] = useState(false);

  const {
    codigoComunidad,
    codigoGrupoEtnico,
    codigoDiscapacidad,
    codigoCondicionMovilidad,
    codigoFactoresRiesgo,
  } = formData.demograficosBeneficiaria;
  const { codigoAtencion } = formData.atencion;
  const { codigoDiagnosticoITS } = formData.manejoITS;

  useEffect(() => {
    const fechaNacimiento = formData.beneficiaria.fechaNacimiento;
    if (fechaNacimiento && dayjs(fechaNacimiento).isValid()) {
      const age = dayjs().diff(dayjs(fechaNacimiento), "year");
      const shouldDisable = age >= 14;
      setIsRepresentanteDisabled(shouldDisable);

      if (shouldDisable) {
        setFormData((prev) => ({
          ...prev,
          representante: {
            primerNombre: "N/A",
            primerApellido: "N/A",
            cedulaIdentidad: "N/A",
          },
        }));
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors.representante;
          return newErrors;
        });
      } else {
        setFormData((prev) => ({
          ...prev,
          representante: {
            primerNombre: "",
            primerApellido: "",
            cedulaIdentidad: "",
          },
        }));
      }
    } else {
      setIsRepresentanteDisabled(false);
      setFormData((prev) => ({
        ...prev,
        representante: {
          primerNombre: "",
          primerApellido: "",
          cedulaIdentidad: "",
        },
      }));
    }
  }, [formData.beneficiaria.fechaNacimiento]);

  useEffect(() => {
    const value = grupoEtnicoMap[codigoComunidad] || "";
    setFormData((prev) => ({
      ...prev,
      demograficosBeneficiaria: {
        ...prev.demograficosBeneficiaria,
        grupoEtnico: value,
      },
    }));
  }, [codigoComunidad]);

  useEffect(() => {
    const value = discapacidadMap[codigoGrupoEtnico] || "";
    setFormData((prev) => ({
      ...prev,
      demograficosBeneficiaria: {
        ...prev.demograficosBeneficiaria,
        discapacidad: value,
      },
    }));
  }, [codigoGrupoEtnico]);

  useEffect(() => {
    const value = condicionMovilidadMap[codigoDiscapacidad] || "";
    setFormData((prev) => ({
      ...prev,
      demograficosBeneficiaria: {
        ...prev.demograficosBeneficiaria,
        condicionMovilidad: value,
      },
    }));
  }, [codigoDiscapacidad]);

  useEffect(() => {
    const value = factoresRiesgoMap[codigoCondicionMovilidad] || "";
    setFormData((prev) => ({
      ...prev,
      demograficosBeneficiaria: {
        ...prev.demograficosBeneficiaria,
        factoresRiesgo: value,
      },
    }));
  }, [codigoCondicionMovilidad]);

  useEffect(() => {
    const value = nivelEducativoMap[codigoFactoresRiesgo] || "";
    setFormData((prev) => ({
      ...prev,
      demograficosBeneficiaria: {
        ...prev.demograficosBeneficiaria,
        nivelEducativo: value,
      },
    }));
  }, [codigoFactoresRiesgo]);

  useEffect(() => {
    const value = metodoAnticonceptivoMap[codigoAtencion] || "";
    setFormData((prev) => ({
      ...prev,
      atencion: { ...prev.atencion, metodoAnticonceptivo: value },
    }));
  }, [codigoAtencion]);

  useEffect(() => {
    const value = diagnosticoItsMap[codigoDiagnosticoITS] || "";
    setFormData((prev) => ({
      ...prev,
      manejoITS: { ...prev.manejoITS, diagnosticoITS: value },
    }));
  }, [codigoDiagnosticoITS]);

  const validate = () => {
    const newErrors = {
      beneficiaria: {},
      representante: {},
      demograficosBeneficiaria: {},
      atencion: {},
      manejoITS: {},
      adicionales: {},
    };
    const requiredBeneficiaria = [
      "codigo",
      "primerNombre",
      "primerApellido",
      "genero",
      "fechaNacimiento",
    ];
    requiredBeneficiaria.forEach((field) => {
      if (!formData.beneficiaria[field])
        newErrors.beneficiaria[field] = "Campo obligatorio";
    });

    if (!isRepresentanteDisabled) {
      const requiredRepresentante = ["primerNombre", "primerApellido"];
      requiredRepresentante.forEach((field) => {
        if (!formData.representante[field])
          newErrors.representante[field] = "Campo obligatorio";
      });
    }

    const requiredDemograficos = [
      "lgbtqi",
      "comunidad",
      "codigoComunidad",
      "grupoEtnico",
      "codigoGrupoEtnico",
      "discapacidad",
      "codigoDiscapacidad",
      "condicionMovilidad",
      "codigoCondicionMovilidad",
      "factoresRiesgo",
      "codigoFactoresRiesgo",
      "nivelEducativo",
      "ingresosPropios",
      "cabezaFamilia",
    ];
    requiredDemograficos.forEach((field) => {
      if (!formData.demograficosBeneficiaria[field])
        newErrors.demograficosBeneficiaria[field] = "Campo obligatorio";
    });
    const requiredAtencion = [
      "pruebaEmbarazo",
      "codigoAtencion",
      "metodoAnticonceptivo",
    ];
    requiredAtencion.forEach((field) => {
      if (!formData.atencion[field])
        newErrors.atencion[field] = "Campo obligatorio";
    });
    const requiredITS = [
      "pruebaVIH",
      "pruebaSifilis",
      "pruebaHepatitis",
      "codigoDiagnosticoITS",
      "tratamientoITS",
    ];
    requiredITS.forEach((field) => {
      if (!formData.manejoITS[field])
        newErrors.manejoITS[field] = "Campo obligatorio";
    });
    if (!formData.adicionales.medicoResponsable) {
      newErrors.adicionales.medicoResponsable = "Campo obligatorio";
    }

    return newErrors;
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const validationErrors = validate();
    setErrors(validationErrors);
    const isFormValid = !Object.values(validationErrors).some(
      (section) => Object.keys(section).length > 0,
    );
    if (isFormValid) {
      const cleanedData = trimFormData(formData);
      console.log("Formulario Válido. Enviando datos limpios:", cleanedData);
    } else {
      console.log("Errores de validación encontrados.");
    }
  };

  const handleChange = (section, e) => {
    const { name, value } = e.target;

    setFormData((prev) => {
      const newSectionData = { ...prev[section] };
      let updatedValue = value;

      const fieldsToCapitalize = [
        "primerNombre",
        "primerApellido",
        "comunidad",
        "diagnosticoITS",
        "comentarios",
      ];
      const numericFields = [
        "codigoComunidad",
        "codigoGrupoEtnico",
        "codigoDiscapacidad",
        "codigoCondicionMovilidad",
        "codigoFactoresRiesgo",
        "codigoAtencion",
        "codigoDiagnosticoITS",
      ];

      if (
        numericFields.includes(name) &&
        value !== "" &&
        !/^\d+$/.test(value)
      ) {
        return prev;
      }

      if (fieldsToCapitalize.includes(name)) {
        updatedValue = capitalizeWords(value);
      } else if (name === "cedulaIdentidad") {
        updatedValue = value.slice(0, 10);
      }

      newSectionData[name] = updatedValue;
      return { ...prev, [section]: newSectionData };
    });

    setErrors((prev) => {
      if (!prev[section]?.[name]) return prev;
      const newSectionErrors = { ...prev[section] };
      delete newSectionErrors[name];
      return { ...prev, [section]: newSectionErrors };
    });
  };

  const handleAutocompleteChange = (section, name, newValue) => {
    setFormData((prev) => ({
      ...prev,
      [section]: { ...prev[section], [name]: newValue },
    }));
    setErrors((prev) => {
      if (!prev[section]?.[name]) return prev;
      const newSectionErrors = { ...prev[section] };
      delete newSectionErrors[name];
      return { ...prev, [section]: newSectionErrors };
    });
  };

  const handleDateChange = (date) => {
    setFormData((p) => ({
      ...p,
      beneficiaria: { ...p.beneficiaria, fechaNacimiento: date },
    }));
    setErrors((prev) => {
      if (!prev.beneficiaria?.fechaNacimiento) return prev;
      const newBeneficiariaErrors = { ...prev.beneficiaria };
      delete newBeneficiariaErrors.fechaNacimiento;
      return { ...prev, beneficiaria: newBeneficiariaErrors };
    });
  };

  const formatPhoneNumber = (value) => {
    if (!value) return "";
    const d = value.replace(/\D/g, "");
    let f = "";
    if (d.length > 0) f += "+" + d.substring(0, 2);
    if (d.length > 2) f += "(" + d.substring(2, 5);
    if (d.length > 5) f += ")" + d.substring(5, 12);
    return f;
  };

  const handlePhoneChange = (e) => {
    const formattedValue = formatPhoneNumber(e.target.value);
    setFormData((p) => ({
      ...p,
      beneficiaria: { ...p.beneficiaria, numeroTelefonico: formattedValue },
    }));
  };

  function handleClickBreadCrumbs(event) {
    event.preventDefault();
  }

  //Modal Handlers
  const [open, setOpen] = React.useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  //Modal Styles
  const style = {
    position: "inherit",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "25%",
    bgcolor: theme.palette.background.alt,
    border: "2px solid #000",
    borderRadius: 4,
    boxShadow: 1,
    p: 4,
  };

  //Routes
  const handleLinkCancelar = (event, message) => {
    if (message === "cancelar") {
      nav(-1);
    }
  };

  const handleLinkHome = (event, message) => {
    if (message === "home") {
      nav("/inicio");
    }
  };

  const handleLinkTranscripcion = (event, message) => {
    if (message === "transcripcion") {
      nav("/transcripcion");
    }
  };

  const handleLinkConvos = (event, message) => {
    if (message === "convos") {
      nav(-1);
    }
  };

  return (
    <Box m="1.5rem 2.5rem">
      {rol === "1" ||
      rol === "2" ||
      rol === "6" ||
      rol === "8" ||
      rol === "9" ||
      rol === "10" ||
      rol === "11" ? (
        <>
          <Box
            role="presentation"
            onClick={handleClickBreadCrumbs}
            sx={{ mb: 3 }}
          >
            <Breadcrumbs aria-label="breadcrumb">
              <Link
                underline="hover"
                sx={{ display: "flex", alignItems: "center" }}
                color="inherit"
                href="/inicio"
                onClick={(event) => handleLinkHome(event, "home")}
              >
                <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                Inicio
              </Link>
              <Link
                underline="hover"
                sx={{ display: "flex", alignItems: "center" }}
                color="inherit"
                href="/transcripcion"
                onClick={(event) =>
                  handleLinkTranscripcion(event, "transcripcion")
                }
              >
                <DriveFileRenameOutlineIcon
                  sx={{ mr: 0.5 }}
                  fontSize="inherit"
                />
                Transcripción
              </Link>
              <Link
                underline="hover"
                sx={{ display: "flex", alignItems: "center" }}
                color="inherit"
                href="/listaTranscripcion"
                onClick={(event) => handleLinkConvos(event, "convos")}
              >
                <AssignmentIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                Convocatoria
              </Link>
              <Typography
                sx={{ display: "flex", alignItems: "center" }}
                color="text.primary"
              >
                <Face3Icon sx={{ mr: 0.5 }} fontSize="inherit" />
                Nueva Beneficiaria
              </Typography>
            </Breadcrumbs>
            <Typography variant="h3" sx={{ fontWeight: "bold", mt: 2 }}>
              Nueva Beneficiaria (Métodos Anticonceptivos)
            </Typography>
          </Box>
          {/*/////////////////  FORMULARIO  ////////////////////*/}
          <Container>
            <Card
              sx={{
                borderRadius: 8,
                boxShadow: 5,
                bgcolor: theme.palette.background.alt,
              }}
            >
              <CardContent>
                <Box
                  component="form"
                  onSubmit={handleSubmit}
                  sx={{ flexGrow: 1, padding: 3 }}
                >
                  <Divider sx={{ mt: 4, mb: 3 }}>
                    <Chip label="INFO. GENERAL" />
                  </Divider>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        required
                        fullWidth
                        label="Estado"
                        value={estadoSalud}
                        disabled
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        required
                        fullWidth
                        label="Municipio"
                        value={municipioSalud}
                        disabled
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        required
                        fullWidth
                        label="Parroquia"
                        value={parroquiaSalud}
                        disabled
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        required
                        fullWidth
                        label="Comunidad"
                        value={estSalud}
                        disabled
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <LocalizationProvider
                        dateAdapter={AdapterDayjs}
                        adapterLocale="es"
                      >
                        <DesktopDatePicker
                          label="Fecha de Atención"
                          inputFormat="DD/MM/YYYY"
                          value={fechaActSalud}
                          onChange={setFechaActSalud}
                          disabled
                          renderInput={(params) => (
                            <TextField fullWidth {...params} />
                          )}
                        />
                      </LocalizationProvider>
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 2 }}>
                    <Chip
                      label="Datos de la Beneficiaria"
                      sx={{ textTransform: "uppercase" }}
                    />
                  </Divider>
                  <Grid container spacing={1}>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Código Beneficiaria"
                        name="codigo"
                        value={formData.beneficiaria.codigo}
                        onChange={(e) => handleChange("beneficiaria", e)}
                        margin="dense"
                        error={!!errors.beneficiaria?.codigo}
                        helperText={errors.beneficiaria?.codigo || ""}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Primer Nombre"
                        name="primerNombre"
                        value={formData.beneficiaria.primerNombre}
                        onChange={(e) => handleChange("beneficiaria", e)}
                        margin="dense"
                        error={!!errors.beneficiaria?.primerNombre}
                        helperText={errors.beneficiaria?.primerNombre || ""}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Primer Apellido"
                        name="primerApellido"
                        value={formData.beneficiaria.primerApellido}
                        onChange={(e) => handleChange("beneficiaria", e)}
                        margin="dense"
                        error={!!errors.beneficiaria?.primerApellido}
                        helperText={errors.beneficiaria?.primerApellido || ""}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormControl
                        fullWidth
                        margin="dense"
                        error={!!errors.beneficiaria?.genero}
                      >
                        <InputLabel id="gen-l">Género</InputLabel>
                        <Select
                          labelId="gen-l"
                          name="genero"
                          value={formData.beneficiaria.genero}
                          label="Género"
                          onChange={(e) => handleChange("beneficiaria", e)}
                        >
                          <MenuItem value="M">M</MenuItem>
                          <MenuItem value="F">F</MenuItem>
                          <MenuItem value="Otro">Otro</MenuItem>
                        </Select>
                        {errors.beneficiaria?.genero && (
                          <FormHelperText>
                            {errors.beneficiaria.genero}
                          </FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Cédula de Identidad"
                        name="cedulaIdentidad"
                        type="number"
                        value={formData.beneficiaria.cedulaIdentidad}
                        onChange={(e) => handleChange("beneficiaria", e)}
                        inputProps={{ maxLength: 10 }}
                        margin="dense"
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Número Telefónico"
                        name="numeroTelefonico"
                        value={formData.beneficiaria.numeroTelefonico}
                        onChange={handlePhoneChange}
                        placeholder="+XX(XXX)XXXXXXX"
                        margin="dense"
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          label="Fecha de Nacimiento"
                          value={formData.beneficiaria.fechaNacimiento}
                          onChange={handleDateChange}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              fullWidth
                              margin="dense"
                              error={!!errors.beneficiaria?.fechaNacimiento}
                              helperText={
                                errors.beneficiaria?.fechaNacimiento || ""
                              }
                            />
                          )}
                        />
                      </LocalizationProvider>
                    </Grid>
                  </Grid>
                  <Divider sx={{ my: 4 }}>
                    <Chip
                      label="Datos del Representante"
                      sx={{ textTransform: "uppercase" }}
                    />
                  </Divider>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        disabled={isRepresentanteDisabled}
                        label="Primer Nombre"
                        name="primerNombre"
                        value={formData.representante.primerNombre}
                        onChange={(e) => handleChange("representante", e)}
                        margin="dense"
                        error={!!errors.representante?.primerNombre}
                        helperText={errors.representante?.primerNombre || ""}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        disabled={isRepresentanteDisabled}
                        label="Primer Apellido"
                        name="primerApellido"
                        value={formData.representante.primerApellido}
                        onChange={(e) => handleChange("representante", e)}
                        margin="dense"
                        error={!!errors.representante?.primerApellido}
                        helperText={errors.representante?.primerApellido || ""}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        disabled={isRepresentanteDisabled}
                        label="Cédula de Identidad"
                        name="cedulaIdentidad"
                        type={isRepresentanteDisabled ? "text" : "number"}
                        value={formData.representante.cedulaIdentidad}
                        onChange={(e) => handleChange("representante", e)}
                        inputProps={{ maxLength: 10 }}
                        margin="dense"
                      />
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 4 }}>
                    <Chip
                      label="Datos Demográficos de la Beneficiaria"
                      sx={{ textTransform: "uppercase" }}
                    />
                  </Divider>
                  <Grid container spacing={1}>
                    <Grid item xs={12} sm={3}>
                      <FormControl
                        fullWidth
                        margin="dense"
                        error={!!errors.demograficosBeneficiaria?.lgbtqi}
                      >
                        <InputLabel id="lgbtqi-l">LGBTQI+</InputLabel>
                        <Select
                          labelId="lgbtqi-l"
                          name="lgbtqi"
                          value={formData.demograficosBeneficiaria.lgbtqi}
                          label="LGBTQI+"
                          onChange={(e) =>
                            handleChange("demograficosBeneficiaria", e)
                          }
                        >
                          <MenuItem value="Si">Si</MenuItem>
                          <MenuItem value="No">No</MenuItem>
                        </Select>
                        {errors.demograficosBeneficiaria?.lgbtqi && (
                          <FormHelperText>
                            {errors.demograficosBeneficiaria.lgbtqi}
                          </FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        fullWidth
                        label="Comunidad"
                        name="comunidad"
                        value={formData.demograficosBeneficiaria.comunidad}
                        onChange={(e) =>
                          handleChange("demograficosBeneficiaria", e)
                        }
                        margin="dense"
                        error={!!errors.demograficosBeneficiaria?.comunidad}
                        helperText={
                          errors.demograficosBeneficiaria?.comunidad || ""
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        fullWidth
                        label="Código Grupo Étnico"
                        name="codigoComunidad"
                        value={
                          formData.demograficosBeneficiaria.codigoComunidad
                        }
                        onChange={(e) =>
                          handleChange("demograficosBeneficiaria", e)
                        }
                        margin="dense"
                        error={
                          !!errors.demograficosBeneficiaria?.codigoComunidad
                        }
                        helperText={
                          errors.demograficosBeneficiaria?.codigoComunidad ||
                          `(Grupo Étnico) ${grupoEtnicoRange.min}-${grupoEtnicoRange.max}`
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        fullWidth
                        disabled
                        label="Grupo Étnico"
                        name="grupoEtnico"
                        value={formData.demograficosBeneficiaria.grupoEtnico}
                        margin="dense"
                        error={!!errors.demograficosBeneficiaria?.grupoEtnico}
                        helperText={
                          errors.demograficosBeneficiaria?.grupoEtnico || ""
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        fullWidth
                        label="Código Discapacidad"
                        name="codigoGrupoEtnico"
                        value={
                          formData.demograficosBeneficiaria.codigoGrupoEtnico
                        }
                        onChange={(e) =>
                          handleChange("demograficosBeneficiaria", e)
                        }
                        margin="dense"
                        error={
                          !!errors.demograficosBeneficiaria?.codigoGrupoEtnico
                        }
                        helperText={
                          errors.demograficosBeneficiaria?.codigoGrupoEtnico ||
                          `(Discapacidad) ${discapacidadRange.min}-${discapacidadRange.max}`
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        fullWidth
                        disabled
                        label="Discapacidad"
                        name="discapacidad"
                        value={formData.demograficosBeneficiaria.discapacidad}
                        margin="dense"
                        error={!!errors.demograficosBeneficiaria?.discapacidad}
                        helperText={
                          errors.demograficosBeneficiaria?.discapacidad || ""
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        fullWidth
                        label="Código Condición de Movilidad"
                        name="codigoDiscapacidad"
                        value={
                          formData.demograficosBeneficiaria.codigoDiscapacidad
                        }
                        onChange={(e) =>
                          handleChange("demograficosBeneficiaria", e)
                        }
                        margin="dense"
                        error={
                          !!errors.demograficosBeneficiaria?.codigoDiscapacidad
                        }
                        helperText={
                          errors.demograficosBeneficiaria?.codigoDiscapacidad ||
                          `(Cond. Movilidad) ${condicionMovilidadRange.min}-${condicionMovilidadRange.max}`
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        fullWidth
                        disabled
                        label="Condición de Movilidad"
                        name="condicionMovilidad"
                        value={
                          formData.demograficosBeneficiaria.condicionMovilidad
                        }
                        margin="dense"
                        error={
                          !!errors.demograficosBeneficiaria?.condicionMovilidad
                        }
                        helperText={
                          errors.demograficosBeneficiaria?.condicionMovilidad ||
                          ""
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        fullWidth
                        label="Código Factores de Riesgo"
                        name="codigoCondicionMovilidad"
                        value={
                          formData.demograficosBeneficiaria
                            .codigoCondicionMovilidad
                        }
                        onChange={(e) =>
                          handleChange("demograficosBeneficiaria", e)
                        }
                        margin="dense"
                        error={
                          !!errors.demograficosBeneficiaria
                            ?.codigoCondicionMovilidad
                        }
                        helperText={
                          errors.demograficosBeneficiaria
                            ?.codigoCondicionMovilidad ||
                          `(Factor Riesgo) ${factoresRiesgoRange.min}-${factoresRiesgoRange.max}`
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        fullWidth
                        disabled
                        label="Factores de Riesgo"
                        name="factoresRiesgo"
                        value={formData.demograficosBeneficiaria.factoresRiesgo}
                        margin="dense"
                        error={
                          !!errors.demograficosBeneficiaria?.factoresRiesgo
                        }
                        helperText={
                          errors.demograficosBeneficiaria?.factoresRiesgo || ""
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        fullWidth
                        label="Código Nivel Educativo"
                        name="codigoFactoresRiesgo"
                        value={
                          formData.demograficosBeneficiaria.codigoFactoresRiesgo
                        }
                        onChange={(e) =>
                          handleChange("demograficosBeneficiaria", e)
                        }
                        margin="dense"
                        error={
                          !!errors.demograficosBeneficiaria
                            ?.codigoFactoresRiesgo
                        }
                        helperText={
                          errors.demograficosBeneficiaria
                            ?.codigoFactoresRiesgo ||
                          `(Nivel Educativo) ${nivelEducativoRange.min}-${nivelEducativoRange.max}`
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        fullWidth
                        disabled
                        label="Nivel Educativo"
                        name="nivelEducativo"
                        value={formData.demograficosBeneficiaria.nivelEducativo}
                        margin="dense"
                        error={
                          !!errors.demograficosBeneficiaria?.nivelEducativo
                        }
                        helperText={
                          errors.demograficosBeneficiaria?.nivelEducativo || ""
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <FormControl
                        fullWidth
                        margin="dense"
                        error={
                          !!errors.demograficosBeneficiaria?.ingresosPropios
                        }
                      >
                        <InputLabel id="ingresos-l">
                          Ingresos Propios
                        </InputLabel>
                        <Select
                          labelId="ingresos-l"
                          name="ingresosPropios"
                          value={
                            formData.demograficosBeneficiaria.ingresosPropios
                          }
                          label="Ingresos Propios"
                          onChange={(e) =>
                            handleChange("demograficosBeneficiaria", e)
                          }
                        >
                          <MenuItem value="Si">Si</MenuItem>
                          <MenuItem value="No">No</MenuItem>
                        </Select>
                        {errors.demograficosBeneficiaria?.ingresosPropios && (
                          <FormHelperText>
                            {errors.demograficosBeneficiaria.ingresosPropios}
                          </FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <FormControl
                        fullWidth
                        margin="dense"
                        error={!!errors.demograficosBeneficiaria?.cabezaFamilia}
                      >
                        <InputLabel id="cabeza-l">Cabeza de Familia</InputLabel>
                        <Select
                          labelId="cabeza-l"
                          name="cabezaFamilia"
                          value={
                            formData.demograficosBeneficiaria.cabezaFamilia
                          }
                          label="Cabeza de Familia"
                          onChange={(e) =>
                            handleChange("demograficosBeneficiaria", e)
                          }
                        >
                          <MenuItem value="Si">Si</MenuItem>
                          <MenuItem value="No">No</MenuItem>
                        </Select>
                        {errors.demograficosBeneficiaria?.cabezaFamilia && (
                          <FormHelperText>
                            {errors.demograficosBeneficiaria.cabezaFamilia}
                          </FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 4 }}>
                    <Chip
                      label="Datos de la atención"
                      sx={{ textTransform: "uppercase" }}
                    />
                  </Divider>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={4}>
                      <FormControl
                        fullWidth
                        margin="dense"
                        error={!!errors.atencion?.pruebaEmbarazo}
                      >
                        <InputLabel id="prueba-emb-l">
                          Prueba de Embarazo
                        </InputLabel>
                        <Select
                          labelId="prueba-emb-l"
                          name="pruebaEmbarazo"
                          value={formData.atencion.pruebaEmbarazo}
                          label="Prueba de Embarazo"
                          onChange={(e) => handleChange("atencion", e)}
                        >
                          <MenuItem value="Positivo">Positivo</MenuItem>
                          <MenuItem value="Negativo">Negativo</MenuItem>
                          <MenuItem value="No Aplica">No Aplica</MenuItem>
                        </Select>
                        {errors.atencion?.pruebaEmbarazo && (
                          <FormHelperText>
                            {errors.atencion.pruebaEmbarazo}
                          </FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Código Método Anticonceptivo"
                        name="codigoAtencion"
                        value={formData.atencion.codigoAtencion}
                        onChange={(e) => handleChange("atencion", e)}
                        margin="dense"
                        error={!!errors.atencion?.codigoAtencion}
                        helperText={
                          errors.atencion?.codigoAtencion ||
                          `(Método Anticonceptivo) ${metodoAnticonceptivoRange.min}-${metodoAnticonceptivoRange.max}`
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        disabled
                        label="Método Anticonceptivo Colocado"
                        name="metodoAnticonceptivo"
                        value={formData.atencion.metodoAnticonceptivo}
                        margin="dense"
                        error={!!errors.atencion?.metodoAnticonceptivo}
                        helperText={errors.atencion?.metodoAnticonceptivo || ""}
                      />
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 4 }}>
                    <Chip
                      label="Manejo de I.T.S."
                      sx={{ textTransform: "uppercase" }}
                    />
                  </Divider>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={4}>
                      <FormControl
                        fullWidth
                        margin="dense"
                        error={!!errors.manejoITS?.pruebaVIH}
                      >
                        <InputLabel id="vih-l">Prueba Rápida VIH</InputLabel>
                        <Select
                          labelId="vih-l"
                          name="pruebaVIH"
                          value={formData.manejoITS.pruebaVIH}
                          label="Prueba Rápida VIH"
                          onChange={(e) => handleChange("manejoITS", e)}
                        >
                          <MenuItem value="Positivo">Positivo</MenuItem>
                          <MenuItem value="Negativo">Negativo</MenuItem>
                          <MenuItem value="No Aplica">No Aplica</MenuItem>
                        </Select>
                        {errors.manejoITS?.pruebaVIH && (
                          <FormHelperText>
                            {errors.manejoITS.pruebaVIH}
                          </FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormControl
                        fullWidth
                        margin="dense"
                        error={!!errors.manejoITS?.pruebaSifilis}
                      >
                        <InputLabel id="sif-l">
                          Prueba Rápida Sífilis
                        </InputLabel>
                        <Select
                          labelId="sif-l"
                          name="pruebaSifilis"
                          value={formData.manejoITS.pruebaSifilis}
                          label="Prueba Rápida Sífilis"
                          onChange={(e) => handleChange("manejoITS", e)}
                        >
                          <MenuItem value="Positivo">Positivo</MenuItem>
                          <MenuItem value="Negativo">Negativo</MenuItem>
                          <MenuItem value="No Aplica">No Aplica</MenuItem>
                        </Select>
                        {errors.manejoITS?.pruebaSifilis && (
                          <FormHelperText>
                            {errors.manejoITS.pruebaSifilis}
                          </FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormControl
                        fullWidth
                        margin="dense"
                        error={!!errors.manejoITS?.pruebaHepatitis}
                      >
                        <InputLabel id="hep-l">Prueba de Hepatitis</InputLabel>
                        <Select
                          labelId="hep-l"
                          name="pruebaHepatitis"
                          value={formData.manejoITS.pruebaHepatitis}
                          label="Prueba de Hepatitis"
                          onChange={(e) => handleChange("manejoITS", e)}
                        >
                          <MenuItem value="Positivo">Positivo</MenuItem>
                          <MenuItem value="Negativo">Negativo</MenuItem>
                          <MenuItem value="No Aplica">No Aplica</MenuItem>
                        </Select>
                        {errors.manejoITS?.pruebaHepatitis && (
                          <FormHelperText>
                            {errors.manejoITS.pruebaHepatitis}
                          </FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Código Diagnóstico I.T.S."
                        name="codigoDiagnosticoITS"
                        value={formData.manejoITS.codigoDiagnosticoITS}
                        onChange={(e) => handleChange("manejoITS", e)}
                        margin="dense"
                        error={!!errors.manejoITS?.codigoDiagnosticoITS}
                        helperText={
                          errors.manejoITS?.codigoDiagnosticoITS ||
                          `(Diagnóstico I.T.S.) ${diagnosticoItsRange.min}-${diagnosticoItsRange.max}`
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        disabled
                        label="Diagnóstico Sindromático de I.T.S."
                        name="diagnosticoITS"
                        value={formData.manejoITS.diagnosticoITS}
                        onChange={(e) => handleChange("manejoITS", e)}
                        margin="dense"
                        error={!!errors.manejoITS?.diagnosticoITS}
                        helperText={errors.manejoITS?.diagnosticoITS || ""}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormControl
                        fullWidth
                        margin="dense"
                        error={!!errors.manejoITS?.tratamientoITS}
                      >
                        <InputLabel id="trat-l">Tratamiento</InputLabel>
                        <Select
                          labelId="trat-l"
                          name="tratamientoITS"
                          value={formData.manejoITS.tratamientoITS}
                          label="Tratamiento"
                          onChange={(e) => handleChange("manejoITS", e)}
                        ></Select>
                        {errors.manejoITS?.tratamientoITS && (
                          <FormHelperText>
                            {errors.manejoITS.tratamientoITS}
                          </FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 4 }}>
                    <Chip
                      label="Adicionales"
                      sx={{ textTransform: "uppercase" }}
                    />
                  </Divider>
                  <Grid container justifyContent="center" spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Comentarios"
                        name="comentarios"
                        value={formData.adicionales.comentarios}
                        onChange={(e) => handleChange("adicionales", e)}
                        margin="dense"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Autocomplete
                        fullWidth
                        options={medicosDePrueba}
                        value={formData.adicionales.medicoResponsable}
                        onChange={(event, newValue) => {
                          handleAutocompleteChange(
                            "adicionales",
                            "medicoResponsable",
                            newValue,
                          );
                        }}
                        isOptionEqualToValue={(option, value) =>
                          option.label === value.label
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Médico Responsable"
                            margin="dense"
                            error={!!errors.adicionales?.medicoResponsable}
                            helperText={
                              errors.adicionales?.medicoResponsable || ""
                            }
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                  <Divider sx={{ my: 4 }} />
                  <Grid container justifyContent="center">
                    <Grid item xs={12} sm={5}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          gap: 2,
                        }}
                      >
                        <Button
                          variant="contained"
                          color="error"
                          size="large"
                          fullWidth
                          onClick={handleOpen}
                        >
                          {" "}
                          Cancelar{" "}
                        </Button>
                        <Button
                          type="submit"
                          variant="contained"
                          size="large"
                          fullWidth
                          sx={{
                            backgroundColor: "teal",
                            "&:hover": { backgroundColor: "darkcyan" },
                          }}
                        >
                          {" "}
                          Guardar{" "}
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </CardContent>
            </Card>
          </Container>
          {/*////////////////  MODAL CANCELAR  /////////////////*/}
          <Modal
            open={open}
            onClose={handleClose}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
            slotProps={{ backdrop: { style: { opacity: 0.2 } } }}
          >
            <Box sx={style}>
              <Typography variant="h6" className="text-center" sx={{ mb: 1.5 }}>
                ¿Está seguro de cancelar esta operación?
              </Typography>
              <Divider sx={{ mt: 2 }} />
              <Grid
                container
                sx={{ mt: 1 }}
                spacing={1}
                justifyContent="flex-end"
              >
                <Grid item>
                  <Button
                    variant="contained"
                    color="error"
                    onClick={handleClose}
                  >
                    No
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    variant="contained"
                    sx={{ bgcolor: "teal", color: "white" }}
                    onClick={(event) => handleLinkCancelar(event, "cancelar")}
                  >
                    Si
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Modal>
        </>
      ) : (
        <Restricted />
      )}
    </Box>
  );
}

export default AntiConForm;